import { MULTIPLAYER_CONFIG } from "../../config/multiplayer.ts";
import type { PlayerId } from "../../core/ids.ts";
import { createNeutralInputIntent, type InputIntent } from "../../input/intents.ts";
import { stepBoatFleet, type BoatFleetStepResult } from "../boats/index.ts";
import { stepElevatorRide, type ElevatorPlayerOverride } from "../elevators/index.ts";
import { stepInteriorPresence } from "../interiors/index.ts";
import { namedResidentPresentations } from "../neighborhood/index.ts";
import {
  stepPublicTransportFleet,
  type PublicTransportQueries,
} from "../publicTransport/index.ts";
import { completeGameTick, planGameTick } from "../tick.ts";
import type { CharacterMotionRequest, CharacterMotionResolution } from "../../physics/contracts.ts";
import { discoverTraversalSites } from "../traversal/index.ts";
import {
  placePlayerInRoadVehicle,
  stepRoadVehicleFleet,
  type RoadVehicleFleetStepResult,
  type RoadVehicleQueries,
} from "../vehicles/index.ts";
import type { MultiplayerRoomSnapshot } from "./types.ts";

type PlayerOverride = Readonly<{
  kind: "elevator" | "public-transport" | "vehicle" | "boat";
  position: Readonly<{ x: number; y: number; z: number }>;
  facingYaw: number;
  surfaceId: string;
  vehicle?: NonNullable<RoadVehicleFleetStepResult["playerOverride"]>;
}>;

export interface MultiplayerRoomStepOptions {
  readonly publicTransportQueries?: Readonly<PublicTransportQueries>;
  readonly roadVehicleQueries?: Readonly<RoadVehicleQueries>;
  readonly resolveCharacterMotions?: (
    requests: readonly Readonly<CharacterMotionRequest>[],
  ) => readonly Readonly<CharacterMotionResolution>[];
}

const fallbackPublicTransportQueries: PublicTransportQueries = {
  groundHeight: () => null,
  resolveExit: ({ candidates }) => candidates[0] ?? null,
};

function patchPlayerOverride(
  snapshot: MultiplayerRoomSnapshot,
  playerId: PlayerId,
  intent: Readonly<InputIntent>,
  override: PlayerOverride,
): MultiplayerRoomSnapshot {
  if (override.kind === "vehicle" && override.vehicle !== undefined) {
    return {
      ...snapshot,
      game: placePlayerInRoadVehicle(
        snapshot.game,
        playerId,
        intent.lookYaw,
        override.vehicle,
      ),
    };
  }
  const player = snapshot.game.playersById[playerId];
  if (player === undefined) return snapshot;
  return {
    ...snapshot,
    game: {
      ...snapshot.game,
      playersById: {
        ...snapshot.game.playersById,
        [playerId]: {
          ...player,
          position: { ...override.position },
          velocity: { x: 0, y: 0, z: 0 },
          facingYaw: override.facingYaw,
          lookYaw: override.kind === "elevator" ? override.facingYaw : intent.lookYaw,
          isSprinting: false,
          locomotion: {
            ...player.locomotion,
            mode: "grounded",
            grounded: true,
            groundNormal: { x: 0, y: 1, z: 0 },
            groundSlopeRadians: 0,
            surfaceId: override.surfaceId,
            coyoteTicksRemaining: 0,
            jumpBufferTicksRemaining: 0,
            landingResponseTicksRemaining: 0,
            mantleProgress: 0,
            footstepDistanceMetres: 0,
            lastStepHeightMetres: 0,
          },
        },
      },
    },
  };
}

function advanceEnvironment(
  environment: Readonly<MultiplayerRoomSnapshot["environment"]>,
  deltaSeconds: number,
): MultiplayerRoomSnapshot["environment"] {
  if (environment.frozen) return environment;
  return {
    ...environment,
    timeOfDayHours:
      (environment.timeOfDayHours +
        deltaSeconds * 24 / MULTIPLAYER_CONFIG.dayLengthSeconds) % 24,
  };
}

export function stepMultiplayerRoom(
  current: Readonly<MultiplayerRoomSnapshot>,
  submittedIntents: readonly Readonly<InputIntent>[],
  deltaSeconds = 1 / MULTIPLAYER_CONFIG.serverTickRateHz,
  options: Readonly<MultiplayerRoomStepOptions> = {},
): MultiplayerRoomSnapshot {
  const nextTick = current.game.tick + 1;
  const submittedByPlayerId = new Map(
    submittedIntents
      .filter((intent) => intent.tick === nextTick)
      .map((intent) => [intent.playerId, intent] as const),
  );
  const playerIds = Object.keys(current.game.playersById).sort();
  const intentsByPlayerId = new Map<PlayerId, InputIntent>();
  for (const playerId of playerIds) {
    const player = current.game.playersById[playerId];
    if (player === undefined) continue;
    intentsByPlayerId.set(
      playerId,
      submittedByPlayerId.get(playerId) ??
        createNeutralInputIntent(playerId, nextTick, player.lookYaw),
    );
  }

  let roadVehicles = current.roadVehicles;
  let publicTransport = {
    ...current.publicTransport,
    tick: nextTick,
    serviceTimeSeconds:
      current.publicTransport.serviceTimeSeconds + deltaSeconds,
  };
  let boats = current.boats;
  const elevators = { ...current.elevatorByPlayerId };
  const overrides = new Map<PlayerId, PlayerOverride>();
  const walkingIntents: InputIntent[] = [];
  let advancesVehicleTransients = true;

  for (const playerId of playerIds) {
    const player = current.game.playersById[playerId];
    const intent = intentsByPlayerId.get(playerId);
    if (player === undefined || intent === undefined) continue;
    const elevator = stepElevatorRide(
      elevators[playerId] ?? { phase: "idle", rideCount: 0 },
      intent,
      player.position,
      player.facingYaw,
      deltaSeconds,
    );
    elevators[playerId] = elevator.state;
    if (elevator.playerOverride !== null) {
      overrides.set(playerId, {
        kind: "elevator",
        ...elevator.playerOverride,
      });
      walkingIntents.push(createNeutralInputIntent(playerId, nextTick, intent.lookYaw));
      continue;
    }
    const worldIntent = elevator.interactionConsumed
      ? { ...intent, interactPressed: false }
      : intent;
    const transitCanConsumeInteraction =
      roadVehicles.mountByPlayerId[playerId] === undefined &&
      boats.mountedBoatIdByPlayerId[playerId] === undefined;
    const publicTransportResult = stepPublicTransportFleet(
      publicTransport,
      transitCanConsumeInteraction
        ? worldIntent
        : { ...worldIntent, interactPressed: false },
      player.position,
      deltaSeconds,
      options.publicTransportQueries ?? fallbackPublicTransportQueries,
      false,
    );
    publicTransport = publicTransportResult.state;
    if (publicTransportResult.playerOverride !== null) {
      overrides.set(playerId, {
        kind: "public-transport",
        position: publicTransportResult.playerOverride.position,
        facingYaw: publicTransportResult.playerOverride.facingYaw,
        surfaceId: publicTransportResult.playerOverride.surfaceId,
      });
      walkingIntents.push(createNeutralInputIntent(playerId, nextTick, intent.lookYaw));
      continue;
    }
    const roadIntent = publicTransportResult.interactionConsumed
      ? { ...worldIntent, interactPressed: false }
      : worldIntent;
    const vehicle = stepRoadVehicleFleet(
      roadVehicles,
      roadIntent,
      player.position,
      deltaSeconds,
      options.roadVehicleQueries,
      { wetness01: current.environment.surfaceWetness01 },
      advancesVehicleTransients,
    );
    advancesVehicleTransients = false;
    roadVehicles = vehicle.state;
    if (vehicle.playerOverride !== null) {
      overrides.set(playerId, {
        kind: "vehicle",
        position: vehicle.playerOverride.position,
        facingYaw: vehicle.playerOverride.facingYaw,
        surfaceId: vehicle.playerOverride.surfaceId,
        vehicle: vehicle.playerOverride,
      });
      walkingIntents.push(createNeutralInputIntent(playerId, nextTick, intent.lookYaw));
      continue;
    }
    const boatIntent = vehicle.interactionConsumed
      ? { ...roadIntent, interactPressed: false }
      : roadIntent;
    const boat: BoatFleetStepResult = stepBoatFleet(
      boats,
      boatIntent,
      player.position,
      deltaSeconds,
    );
    boats = boat.state;
    if (boat.playerOverride !== null) {
      overrides.set(playerId, {
        kind: "boat",
        position: boat.playerOverride.position,
        facingYaw: boat.playerOverride.facingYaw,
        surfaceId: boat.playerOverride.surfaceId,
      });
      walkingIntents.push(createNeutralInputIntent(playerId, nextTick, intent.lookYaw));
    } else {
      walkingIntents.push(boat.interactionConsumed
        ? { ...boatIntent, interactPressed: false }
        : boatIntent);
    }
  }

  const plan = planGameTick(current.game, walkingIntents, deltaSeconds);
  // Vehicle, boat and elevator occupants are positioned by their owning
  // simulation below. Never ask a character controller to resolve them: the
  // browser deliberately detaches that controller while mounted/travelling.
  const characterMotionRequests = plan.motionRequests.filter(
    (request) => !overrides.has(request.playerId),
  );
  const resolutions = options.resolveCharacterMotions?.(
    characterMotionRequests,
  ) ?? [];
  const completedGame = completeGameTick(current.game, plan, resolutions);
  let snapshot: MultiplayerRoomSnapshot = {
    ...current,
    game: completedGame.state,
    boats,
    roadVehicles,
    publicTransport,
    elevatorByPlayerId: elevators,
    environment: advanceEnvironment(current.environment, deltaSeconds),
    semanticEvents: [
      ...current.semanticEvents,
      ...completedGame.events,
    ].slice(-MULTIPLAYER_CONFIG.semanticEventHistoryLimit),
  };
  for (const playerId of playerIds) {
    const override = overrides.get(playerId);
    const intent = intentsByPlayerId.get(playerId);
    if (override !== undefined && intent !== undefined) {
      snapshot = patchPlayerOverride(snapshot, playerId, intent, override);
    }
  }

  const interiors = { ...snapshot.interiorByPlayerId };
  const traversal = { ...snapshot.traversalByPlayerId };
  for (const playerId of playerIds) {
    const player = snapshot.game.playersById[playerId];
    if (player === undefined) continue;
    interiors[playerId] = stepInteriorPresence(
      interiors[playerId] ?? {
        activeInteriorId: null,
        candidateInteriorId: null,
        candidateTicks: 0,
        outsideTicks: 0,
        entryCount: 0,
        exitCount: 0,
      },
      player.position,
      player.locomotion.mode !== "flying",
    );
    traversal[playerId] = discoverTraversalSites(
      traversal[playerId] ?? { schemaVersion: 1, discoveredSiteIds: [] },
      player.position,
    ).state;
  }
  return {
    ...snapshot,
    interiorByPlayerId: interiors,
    traversalByPlayerId: traversal,
    residents: namedResidentPresentations(snapshot.environment.timeOfDayHours),
  };
}
